import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Box, Container, Stack, TextField, Typography, Button } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PositiveButton from 'components/cmn/PositiveButton';
import CustomCard from 'components/cmn/CustomCard';
import { useAppDispatch } from 'store';
import { openBackdrop, closeBackdrop } from 'store/backdrop/backdropSlice';
import { showMessage } from 'store/message/messageSlice';
import axios, { AxiosError } from 'axios';

interface ExternalRegisterForm {
  userName: string;
  userEmail: string;
  userCompName: string;
  userId: string;
}

function ExternalRegisterPage(): JSX.Element {
  const { projectId } = useParams<{ projectId: string }>();
  const dispatch = useAppDispatch();

  const [formData, setFormData] = useState<ExternalRegisterForm>({
    userName: '',
    userEmail: '',
    userCompName: '',
    userId: '',
  });

  const [registeredUrl, setRegisteredUrl] = useState<string>('');
  const [isAlreadyRegistered, setIsAlreadyRegistered] = useState<boolean>(false);
  const [registrationMessage, setRegistrationMessage] = useState<string>('');

  const handleInputChange = (field: keyof ExternalRegisterForm) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [field]: event.target.value,
    });
  };

  const handleSubmit = async () => {
    // 필수 입력값 검증 (모든 필드)
    if (!formData.userName || !formData.userEmail || !formData.userCompName || !formData.userId) {
      dispatch(showMessage({ message: '모든 필드를 입력해주세요.', variant: 'warning' }));
      return;
    }

    // 이메일 형식 검증
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.userEmail)) {
      dispatch(showMessage({ message: '올바른 이메일 형식을 입력해주세요.', variant: 'warning' }));
      return;
    }

    dispatch(openBackdrop({}));

    try {
      const url = process.env.REACT_APP_URL || 'http://localhost:8080';
      const response = await axios.post(
        `${url}/api/external/project/${projectId}/user/register`,
        {
          userName: formData.userName,
          userEmail: formData.userEmail,
          userCompName: formData.userCompName,
          userId: formData.userId,
          resultCallbackUrl: '',
        },
        {
          headers: {
            'X-API-KEY': 'ak_fdacd43dcbde49a0a9b8e6bc05fbf3c2',
            'Content-Type': 'application/json',
          },
        },
      );

      if (response.data.success) {
        dispatch(
          showMessage({
            message: response.data.message || '사용자가 등록되었습니다.',
            variant: 'success',
          }),
        );

        // detailUrl 저장하여 화면에 표시
        if (response.data.detailUrl) {
          setRegisteredUrl(response.data.detailUrl);
          setIsAlreadyRegistered(false);
          setRegistrationMessage(
            response.data.message || '등록이 완료되었습니다! 아래 링크로 이동하여 테스트를 시작하세요.',
          );
        }
      } else if (response.data.detailUrl) {
        // 이미 등록된 경우 등 실패 시에도 detailUrl이 있으면 링크 표시
        setRegisteredUrl(response.data.detailUrl);
        setIsAlreadyRegistered(true);
        setRegistrationMessage(response.data.message || '이미 등록된 이메일입니다.');
      } else {
        dispatch(
          showMessage({
            message: response.data.message || '등록에 실패했습니다.',
            variant: 'error',
          }),
        );
      }
    } catch (error) {
      console.error('Registration error:', error);
      const axiosError = error as AxiosError<{ message?: string }>;
      const errorMessage = axiosError.response?.data?.message || '사용자 등록 중 오류가 발생했습니다.';
      dispatch(showMessage({ message: errorMessage, variant: 'error' }));
    } finally {
      dispatch(closeBackdrop());
    }
  };

  const handleCopyUrl = () => {
    navigator.clipboard
      .writeText(registeredUrl)
      .then(() => {
        dispatch(showMessage({ message: '링크가 클립보드에 복사되었습니다.', variant: 'success' }));
      })
      .catch(() => {
        dispatch(showMessage({ message: '링크 복사에 실패했습니다.', variant: 'error' }));
      });
  };

  const handleNavigateToUrl = () => {
    if (registeredUrl) {
      window.location.href = registeredUrl;
    }
  };

  // 버튼 활성화 여부 (모든 필드 필수)
  const isFormValid =
    formData.userName.trim() !== '' &&
    formData.userEmail.trim() !== '' &&
    formData.userCompName.trim() !== '' &&
    formData.userId.trim() !== '';

  return (
    <Box
      sx={{
        width: '100%',
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: '#f5f5f5',
        py: 4,
      }}
    >
      <Container maxWidth="sm">
        <CustomCard sx={{ p: 4 }}>
          <Stack spacing={3}>
            <Typography variant="h4" sx={{ textAlign: 'center', mb: 2, fontWeight: 'bold' }}>
              외부 사용자 테스트
            </Typography>

            <TextField
              fullWidth
              label="이름"
              value={formData.userName}
              onChange={handleInputChange('userName')}
              variant="outlined"
              required
            />

            <TextField
              fullWidth
              label="이메일"
              type="email"
              value={formData.userEmail}
              onChange={handleInputChange('userEmail')}
              variant="outlined"
              required
            />

            <TextField
              fullWidth
              label="기업/부서"
              value={formData.userCompName}
              onChange={handleInputChange('userCompName')}
              variant="outlined"
              required
            />

            <TextField
              fullWidth
              label="직급/아이디"
              value={formData.userId}
              onChange={handleInputChange('userId')}
              variant="outlined"
              required
            />

            {registeredUrl && (
              <Box
                sx={{
                  mt: 2,
                  p: 2,
                  bgcolor: isAlreadyRegistered ? '#fff5f5' : '#f0f7ff',
                  borderRadius: 2,
                  border: isAlreadyRegistered ? '1px solid #ef5350' : '1px solid #90caf9',
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{ mb: 1, fontWeight: 'bold', color: isAlreadyRegistered ? '#d32f2f' : '#1976d2' }}
                >
                  {registrationMessage}
                </Typography>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <TextField
                    fullWidth
                    value={registeredUrl}
                    InputProps={{
                      readOnly: true,
                      sx: { fontSize: '0.85rem', bgcolor: 'white' },
                    }}
                    variant="outlined"
                    size="small"
                  />
                </Box>

                <Stack direction="row" spacing={1} justifyContent="center">
                  <Button variant="outlined" startIcon={<ContentCopyIcon />} onClick={handleCopyUrl} sx={{ flex: 1 }}>
                    복사하기
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<OpenInNewIcon />}
                    onClick={handleNavigateToUrl}
                    sx={{ flex: 1 }}
                  >
                    이동하기
                  </Button>
                </Stack>
              </Box>
            )}

            {!registeredUrl && (
              <PositiveButton
                disabled={!isFormValid}
                variant="contained"
                onClick={handleSubmit}
                sx={{
                  width: '100%',
                  height: 52,
                  fontSize: '1.1rem',
                  mt: 2,
                }}
                title="사용자 등록"
              />
            )}
          </Stack>
        </CustomCard>
      </Container>
    </Box>
  );
}

export default ExternalRegisterPage;
